import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit, Trash2, Loader, X, Search, RefreshCw, AlertTriangle,
  Settings, List, Layers, ChevronDown, ChevronUp, Type, Hash, 
  ToggleLeft, ListOrdered, Image, DollarSign
} from 'lucide-react';
import adminService, { 
  Category, CategoryField, FieldType, AddCategoryFieldRequest, 
  UpdateCategoryFieldRequest 
} from '../../services/admin/adminService';
import toast from 'react-hot-toast';
import DataTable, { Column } from '../../components/common/DataTable';

const DynamicFieldsManagement: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [fields, setFields] = useState<CategoryField[]>([]);
  const [loading, setLoading] = useState(true);
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  // Modals
  const [addFieldModal, setAddFieldModal] = useState(false);
  const [editFieldModal, setEditFieldModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState<{ show: boolean; field: CategoryField | null }>({
    show: false,
    field: null
  });
  const [optionsModal, setOptionsModal] = useState<{ show: boolean; field: CategoryField | null }>({
    show: false,
    field: null
  });
  
  // Forms
  const [addFieldForm, setAddFieldForm] = useState<AddCategoryFieldRequest>({
    category_id: 0,
    field_name: '',
    field_type: 'text',
    is_required: false,
    display_order: 0,
    options: []
  });
  
  const [editFieldForm, setEditFieldForm] = useState<UpdateCategoryFieldRequest | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  // Field options management
  const [newOption, setNewOption] = useState('');
  const [addingOption, setAddingOption] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      fetchCategoryFields(selectedCategory);
    }
  }, [selectedCategory]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const data = await adminService.getAllCategories();
      setCategories(data.filter(c => c.is_active === 1));
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategoryFields = async (categoryId: number, showToast = false) => {
    try {
      if (showToast) {
        setRefreshing(true);
      } else {
        setFieldsLoading(true);
      }
      
      const data = await adminService.getCategoryFields(categoryId);
      setFields(data);
      
      if (showToast) {
        toast.success('Fields refreshed!');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch fields');
    } finally {
      setFieldsLoading(false);
      setRefreshing(false);
    }
  };

  const handleAddField = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCategory) {
      toast.error('Please select a category first');
      return;
    }

    try {
      setSaving(true);
      await adminService.addCategoryField({
        ...addFieldForm,
        category_id: selectedCategory
      });
      toast.success('Field added successfully!');
      setAddFieldModal(false);
      setAddFieldForm({
        category_id: 0,
        field_name: '',
        field_type: 'text',
        is_required: false,
        display_order: 0,
        options: []
      });
      fetchCategoryFields(selectedCategory);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to add field');
    } finally {
      setSaving(false);
    }
  };

  const handleEditField = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editFieldForm || !selectedCategory) return;
    
    try {
      setSaving(true);
      await adminService.updateCategoryField(editFieldForm);
      toast.success('Field updated successfully!');
      setEditFieldModal(false);
      setEditFieldForm(null);
      fetchCategoryFields(selectedCategory);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update field');
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = (field: CategoryField) => {
    setEditFieldForm({
      field_id: field.field_id,
      field_name: field.field_name,
      field_type: field.field_type,
      is_required: field.is_required,
      display_order: field.display_order,
      options: field.options.map(opt => opt.option_value)
    });
    setEditFieldModal(true);
  };

  const openDeleteModal = (field: CategoryField) => {
    setDeleteModal({ show: true, field });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ show: false, field: null });
  };

  const confirmDelete = async () => {
    if (!deleteModal.field || !selectedCategory) return;
    
    try {
      setDeleting(true);
      await adminService.deleteCategoryField(deleteModal.field.field_id);
      toast.success('Field deleted successfully!');
      closeDeleteModal();
      fetchCategoryFields(selectedCategory);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete field');
    } finally {
      setDeleting(false);
    }
  };

  const openOptionsModal = (field: CategoryField) => {
    if (field.field_type !== 'dropdown') {
      toast.error('Options can only be managed for dropdown fields');
      return;
    }
    setOptionsModal({ show: true, field });
    setNewOption('');
  };

  const handleAddOption = async () => {
    if (!optionsModal.field || !newOption.trim()) return;
    
    try {
      setAddingOption(true);
      await adminService.addFieldOption({
        field_id: optionsModal.field.field_id,
        option_value: newOption.trim(),
        display_order: optionsModal.field.options.length + 1
      });
      toast.success('Option added successfully!');
      setNewOption('');
      if (selectedCategory) {
        fetchCategoryFields(selectedCategory);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to add option');
    } finally {
      setAddingOption(false);
    }
  };

  const handleDeleteOption = async (optionId: number) => {
    if (!selectedCategory) return;
    
    try {
      await adminService.deleteFieldOption(optionId);
      toast.success('Option deleted successfully!');
      fetchCategoryFields(selectedCategory);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete option');
    }
  };

  const getFieldTypeIcon = (type: FieldType) => {
    switch (type) {
      case 'text': return <Type size={18} className="text-primary-600" />;
      case 'number': return <Hash size={18} className="text-success-600" />;
      case 'boolean': return <ToggleLeft size={18} className="text-warning-600" />;
      case 'dropdown': return <ListOrdered size={18} className="text-info-600" />;
      case 'image': return <Image size={18} className="text-purple-600" />;
      case 'currency': return <DollarSign size={18} className="text-success-600" />;
      default: return <Type size={18} className="text-secondary-600" />;
    }
  };

  const getFieldTypeBadge = (type: FieldType) => {
    const colors: Record<FieldType, string> = {
      text: 'bg-primary-50 text-primary-600',
      number: 'bg-success-50 text-success-600',
      boolean: 'bg-warning-50 text-warning-600',
      dropdown: 'bg-info-50 text-info-600',
      image: 'bg-purple-50 text-purple-600',
      currency: 'bg-success-50 text-success-600'
    };
    return colors[type] || 'bg-secondary-50 text-secondary-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader className="animate-spin text-primary-500" size={48} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900 mb-2">Dynamic Fields Management</h1>
          <p className="text-secondary-600">Create custom fields for each business category</p>
        </div>
        {selectedCategory && (
          <div className="flex space-x-3">
            <button
              onClick={() => fetchCategoryFields(selectedCategory, true)}
              disabled={refreshing}
              className="bg-secondary-50 hover:bg-secondary-100 text-secondary-700 px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-colors disabled:opacity-50"
            >
              <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
              <span>Refresh</span>
            </button>
            <button
              onClick={() => setAddFieldModal(true)}
              className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold flex items-center space-x-2 transition-all hover:scale-105 shadow-green"
            >
              <Plus size={20} />
              <span>Add Field</span>
            </button>
          </div>
        )}
      </div>

      {/* Category Selection */}
      <div className="bg-white rounded-xl shadow-soft p-6">
        <label className="block text-sm font-semibold text-secondary-700 mb-3">
          Select Category to Manage Fields
        </label>
        <select
          value={selectedCategory || ''}
          onChange={(e) => setSelectedCategory(Number(e.target.value))}
          className="w-full px-4 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
        >
          <option value="">-- Choose a category --</option>
          {categories.map((cat) => (
            <option key={cat.category_id} value={cat.category_id}>
              {cat.category_name}
            </option>
          ))}
        </select>
      </div>

      {/* Fields List */}
      {selectedCategory && (
        <div className="bg-white rounded-xl shadow-soft overflow-hidden">
          {fieldsLoading ? (
            <div className="text-center py-20">
              <Loader className="animate-spin text-primary-500 mx-auto mb-4" size={48} />
              <p className="text-secondary-600">Loading fields...</p>
            </div>
          ) : fields.length === 0 ? (
            <div className="text-center py-20">
              <Settings size={64} className="text-secondary-300 mx-auto mb-4" />
              <p className="text-secondary-600 font-semibold mb-2">No fields found</p>
              <p className="text-sm text-secondary-500 mb-6">Start by adding your first custom field</p>
              <button
                onClick={() => setAddFieldModal(true)}
                className="bg-primary-500 text-white px-6 py-3 rounded-lg font-semibold inline-flex items-center space-x-2"
              >
                <Plus size={20} />
                <span>Add First Field</span>
              </button>
            </div>
          ) : (
            <DataTable
              data={fields}
              loading={fieldsLoading}
              searchable={true}
              searchPlaceholder="Search fields by name or slug..."
              searchFields={['field_name', 'field_slug']}
              filterable={true}
              filterOptions={[
                { label: 'Required', value: 'required', field: 'is_required' },
                { label: 'Optional', value: 'optional', field: 'is_required' }
              ]}
              defaultPageSize={10}
              emptyMessage="No fields found"
              emptyIcon={<Settings size={64} className="text-secondary-300 mx-auto mb-4" />}
              columns={[
                {
                  header: 'Field Name',
                  accessor: (field) => (
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                        {getFieldTypeIcon(field.field_type)}
                      </div>
                      <div>
                        <p className="font-semibold text-secondary-900">{field.field_name}</p>
                        <p className="text-xs text-secondary-500">{field.field_slug}</p>
                      </div>
                    </div>
                  ),
                  sortable: true,
                },
                {
                  header: 'Type',
                  accessor: (field) => (
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getFieldTypeBadge(field.field_type)}`}>
                      {field.field_type}
                    </span>
                  ),
                  align: 'center',
                  sortable: true,
                },
                {
                  header: 'Required',
                  accessor: (field) => (
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                      field.is_required 
                        ? 'bg-danger-50 text-danger-600' 
                        : 'bg-secondary-50 text-secondary-600'
                    }`}>
                      {field.is_required ? 'Required' : 'Optional'}
                    </span>
                  ),
                  align: 'center',
                  sortable: true,
                },
                {
                  header: 'Display Order',
                  accessor: (field) => (
                    <span className="inline-flex items-center justify-center w-10 h-10 bg-secondary-100 text-secondary-700 rounded-lg text-sm font-bold">
                      {field.display_order}
                    </span>
                  ),
                  align: 'center',
                  sortable: true,
                },
                {
                  header: 'Options',
                  accessor: (field) => (
                    field.field_type === 'dropdown' ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openOptionsModal(field);
                        }}
                        className="text-info-600 hover:text-info-700 font-semibold text-sm flex items-center justify-center mx-auto space-x-1"
                      >
                        <List size={16} />
                        <span>{field.options.length} options</span>
                      </button>
                    ) : (
                      <span className="text-secondary-400 text-sm">N/A</span>
                    )
                  ),
                  align: 'center',
                },
              ]}
              renderActions={(field) => (
                <div className="flex items-center justify-center space-x-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditModal(field);
                    }}
                    className="bg-warning-50 hover:bg-warning-100 text-warning-600 p-2 rounded-lg transition-colors"
                    title="Edit Field"
                  >
                    <Edit size={18} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openDeleteModal(field);
                    }}
                    className="bg-danger-50 hover:bg-danger-100 text-danger-600 p-2 rounded-lg transition-colors"
                    title="Delete Field"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              )}
            />
          )}
        </div>
      )}

      {/* Add Field Modal */}
      {addFieldModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white">
              <h2 className="text-xl font-bold text-secondary-900">Add New Field</h2>
              <button onClick={() => setAddFieldModal(false)} className="text-secondary-400 hover:text-secondary-600">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleAddField} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-secondary-700 mb-2">
                  Field Name <span className="text-danger-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={addFieldForm.field_name}
                  onChange={(e) => setAddFieldForm({...addFieldForm, field_name: e.target.value})}
                  className="w-full px-4 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                  placeholder="e.g., Petrol Price"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-secondary-700 mb-2">
                  Field Type <span className="text-danger-500">*</span>
                </label>
                <select
                  value={addFieldForm.field_type}
                  onChange={(e) => setAddFieldForm({...addFieldForm, field_type: e.target.value as FieldType})}
                  className="w-full px-4 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                >
                  <option value="text">Text</option>
                  <option value="number">Number</option>
                  <option value="currency">Currency</option>
                  <option value="boolean">Boolean (Yes/No)</option>
                  <option value="dropdown">Dropdown</option>
                  <option value="image">Image Upload</option>
                </select>
              </div>

              {addFieldForm.field_type === 'dropdown' && (
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Dropdown Options (comma separated)
                  </label>
                  <textarea
                    value={addFieldForm.options?.join(', ') || ''}
                    onChange={(e) => setAddFieldForm({
                      ...addFieldForm, 
                      options: e.target.value.split(',').map(opt => opt.trim()).filter(opt => opt)
                    })}
                    className="w-full px-4 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                    placeholder="e.g., Petrol, Diesel, CNG, LPG"
                    rows={3}
                  />
                  <p className="text-xs text-secondary-500 mt-1">Separate each option with a comma</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-secondary-700 mb-2">
                  Display Order
                </label>
                <input
                  type="number"
                  value={addFieldForm.display_order}
                  onChange={(e) => setAddFieldForm({...addFieldForm, display_order: parseInt(e.target.value) || 0})}
                  className="w-full px-4 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is_required_add"
                  checked={addFieldForm.is_required}
                  onChange={(e) => setAddFieldForm({...addFieldForm, is_required: e.target.checked})}
                  className="w-4 h-4"
                />
                <label htmlFor="is_required_add" className="text-sm font-semibold text-secondary-700">
                  This field is required
                </label>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setAddFieldModal(false)}
                  className="flex-1 px-6 py-3 bg-secondary-200 hover:bg-secondary-300 text-secondary-900 rounded-lg font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-semibold flex items-center justify-center space-x-2 disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <Loader className="animate-spin" size={20} />
                      <span>Adding...</span>
                    </>
                  ) : (
                    <>
                      <Plus size={20} />
                      <span>Add Field</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Field Modal */}
      {editFieldModal && editFieldForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white">
              <h2 className="text-xl font-bold text-secondary-900">Edit Field</h2>
              <button onClick={() => setEditFieldModal(false)} className="text-secondary-400 hover:text-secondary-600">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleEditField} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-secondary-700 mb-2">
                  Field Name <span className="text-danger-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={editFieldForm.field_name}
                  onChange={(e) => setEditFieldForm({...editFieldForm, field_name: e.target.value})}
                  className="w-full px-4 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-secondary-700 mb-2">
                  Field Type <span className="text-danger-500">*</span>
                </label>
                <select
                  value={editFieldForm.field_type}
                  onChange={(e) => setEditFieldForm({...editFieldForm, field_type: e.target.value as FieldType})}
                  className="w-full px-4 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                >
                  <option value="text">Text</option>
                  <option value="number">Number</option>
                  <option value="currency">Currency</option>
                  <option value="boolean">Boolean (Yes/No)</option>
                  <option value="dropdown">Dropdown</option>
                  <option value="image">Image Upload</option>
                </select>
              </div>

              {editFieldForm.field_type === 'dropdown' && (
                <div>
                  <label className="block text-sm font-semibold text-secondary-700 mb-2">
                    Dropdown Options (comma separated)
                  </label>
                  <textarea
                    value={editFieldForm.options?.join(', ') || ''}
                    onChange={(e) => setEditFieldForm({
                      ...editFieldForm, 
                      options: e.target.value.split(',').map(opt => opt.trim()).filter(opt => opt)
                    })}
                    className="w-full px-4 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                    placeholder="e.g., Petrol, Diesel, CNG, LPG"
                    rows={3}
                  />
                  <p className="text-xs text-secondary-500 mt-1">Separate each option with a comma</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-secondary-700 mb-2">
                  Display Order
                </label>
                <input
                  type="number"
                  value={editFieldForm.display_order}
                  onChange={(e) => setEditFieldForm({...editFieldForm, display_order: parseInt(e.target.value) || 0})}
                  className="w-full px-4 py-3 border-2 border-secondary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is_required_edit"
                  checked={editFieldForm.is_required}
                  onChange={(e) => setEditFieldForm({...editFieldForm, is_required: e.target.checked})}
                  className="w-4 h-4"
                />
                <label htmlFor="is_required_edit" className="text-sm font-semibold text-secondary-700">
                  This field is required
                </label>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setEditFieldModal(false)}
                  className="flex-1 px-6 py-3 bg-secondary-200 hover:bg-secondary-300 text-secondary-900 rounded-lg font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-lg font-semibold flex items-center justify-center space-x-2 disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <Loader className="animate-spin" size={20} />
                      <span>Updating...</span>
                    </>
                  ) : (
                    <>
                      <Edit size={20} />
                      <span>Update Field</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.show && deleteModal.field && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center space-x-3">
                <div className="bg-danger-100 p-3 rounded-full">
                  <AlertTriangle className="text-danger-600" size={24} />
                </div>
                <h2 className="text-xl font-bold text-secondary-900">Delete Field</h2>
              </div>
              <button onClick={closeDeleteModal} disabled={deleting} className="text-secondary-400 hover:text-secondary-600">
                <X size={24} />
              </button>
            </div>
            <div className="p-6">
              <p className="text-secondary-700 mb-4">
                Are you sure you want to delete <strong>{deleteModal.field.field_name}</strong>? This action cannot be undone.
              </p>
            </div>
            <div className="flex space-x-3 p-6 bg-secondary-50 rounded-b-2xl">
              <button
                onClick={closeDeleteModal}
                disabled={deleting}
                className="flex-1 px-6 py-3 bg-white border-2 border-secondary-300 text-secondary-700 rounded-lg font-semibold hover:bg-secondary-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="flex-1 px-6 py-3 bg-danger-500 hover:bg-danger-600 text-white rounded-lg font-semibold flex items-center justify-center space-x-2 disabled:opacity-50"
              >
                {deleting ? (
                  <>
                    <Loader className="animate-spin" size={20} />
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <Trash2 size={20} />
                    <span>Delete Field</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Options Management Modal */}
      {optionsModal.show && optionsModal.field && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white">
              <div>
                <h2 className="text-xl font-bold text-secondary-900">Manage Options</h2>
                <p className="text-sm text-secondary-600 mt-1">{optionsModal.field.field_name}</p>
              </div>
              <button 
                onClick={() => setOptionsModal({ show: false, field: null })} 
                className="text-secondary-400 hover:text-secondary-600"
              >
                <X size={24} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* Add New Option */}
              <div className="bg-primary-50 p-4 rounded-lg">
                <label className="block text-sm font-semibold text-secondary-700 mb-2">
                  Add New Option
                </label>
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={newOption}
                    onChange={(e) => setNewOption(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddOption()}
                    className="flex-1 px-4 py-2 border-2 border-primary-200 rounded-lg focus:border-primary-500 focus:outline-none"
                    placeholder="e.g., Petrol"
                  />
                  <button
                    onClick={handleAddOption}
                    disabled={addingOption || !newOption.trim()}
                    className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-lg font-semibold disabled:opacity-50"
                  >
                    {addingOption ? <Loader className="animate-spin" size={20} /> : <Plus size={20} />}
                  </button>
                </div>
              </div>

              {/* Existing Options */}
              <div>
                <h3 className="text-sm font-semibold text-secondary-700 mb-3">
                  Current Options ({optionsModal.field.options.length})
                </h3>
                {optionsModal.field.options.length === 0 ? (
                  <div className="text-center py-8 text-secondary-500">
                    <List size={48} className="mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No options added yet</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {optionsModal.field.options.map((option) => (
                      <div key={option.option_id} className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg">
                        <span className="font-medium text-secondary-900">{option.option_value}</span>
                        <button
                          onClick={() => handleDeleteOption(option.option_id)}
                          className="text-danger-600 hover:text-danger-700 p-1"
                          title="Delete option"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="p-6 bg-secondary-50 rounded-b-2xl">
              <button
                onClick={() => setOptionsModal({ show: false, field: null })}
                className="w-full px-6 py-3 bg-secondary-700 hover:bg-secondary-800 text-white rounded-lg font-semibold"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DynamicFieldsManagement;

